import {ConnectionHandler, RecordSourceProxy} from 'relay-runtime';

/**
 * A deleted task's set membership, supplied by a caller whose tasks are not Relay records (the
 * Timeline's bars), so the store alone cannot say which set the task belonged to.
 */
export interface TaskSetInstanceHint {
	taskId: string;
	taskSetId: string;
	/** The task's 1-based position in its set, or null when it is not known. */
	position: number | null;
}

export interface TaskSetDeleteAdjustment {
	/** The ids the server reports as deleted. Null or empty means nothing was deleted. */
	deletedIds: ReadonlyArray<string> | null | undefined;
	/** The project whose `Project_tasks` connection holds the deleted task's siblings. */
	projectId?: string | null;
	taskSetInstances?: ReadonlyArray<TaskSetInstanceHint> | null;
}

interface SetRemoval {
	count: number;
	positions: number[];
}

/**
 * The position a surviving task moves to once the tasks at `removed` are gone from its set: down
 * by one for every removed position below it. This is the same derivation the server makes, so
 * the two agree whenever the positions the client held were current.
 */
export const shiftedPosition = (position: number, removed: ReadonlyArray<number>): number =>
	position - removed.filter(removedPosition => removedPosition < position).length;

const numberOrNull = (value: unknown): number | null => (typeof value === 'number' ? value : null);

const membershipOf = (
	store: RecordSourceProxy,
	id: string,
	hints: ReadonlyArray<TaskSetInstanceHint> | null | undefined
): {setId: string; position: number | null} | null => {
	// A set linked in the store wins. A hint stands in for a task the store does not link to a set,
	// whether the store has no record of it or holds one whose taskSet is missing or null.
	const task = store.get(id);
	const taskSet = task?.getLinkedRecord('taskSet');
	if (taskSet) {
		return {setId: taskSet.getDataID(), position: numberOrNull(task?.getValue('taskSetSequence'))};
	}
	const hint = hints?.find(candidate => candidate.taskId === id);
	return hint ? {setId: hint.taskSetId, position: hint.position} : null;
};

/**
 * Brings the store's task-set chips in line with a delete without asking the server: each
 * affected set's `instanceCount` drops by the number of its tasks that were deleted, and each
 * surviving sibling on the project's `Project_tasks` connection closes the gaps below it.
 *
 * Run it from a mutation updater, which Relay calls before the declarative RANGE_DELETE configs,
 * so the deleted tasks are still readable here and may still sit on the connection; they are
 * skipped explicitly. A deleted task whose position is unknown still lowers the count but shifts
 * nothing.
 */
export const adjustTaskSetsAfterDelete = (
	store: RecordSourceProxy,
	{deletedIds, projectId, taskSetInstances}: TaskSetDeleteAdjustment
): void => {
	if (!deletedIds || deletedIds.length === 0) {
		return;
	}

	// A set, so an id the response repeats is removed from its set once, not twice.
	const deleted = new Set(deletedIds);
	const removals = new Map<string, SetRemoval>();
	deleted.forEach(id => {
		const membership = membershipOf(store, id, taskSetInstances);
		if (!membership) {
			return;
		}
		const removal = removals.get(membership.setId) ?? {count: 0, positions: []};
		removal.count += 1;
		if (membership.position != null) {
			removal.positions.push(membership.position);
		}
		removals.set(membership.setId, removal);
	});

	removals.forEach((removal, setId) => {
		const taskSet = store.get(setId);
		const instanceCount = numberOrNull(taskSet?.getValue('instanceCount'));
		if (taskSet && instanceCount != null) {
			taskSet.setValue(Math.max(0, instanceCount - removal.count), 'instanceCount');
		}
	});

	// Walking the list is the costly part (Scoping holds every task of the project), so skip it
	// when no deleted task had a known position to shift past.
	const anyPositions = Array.from(removals.values()).some(removal => removal.positions.length > 0);
	if (!anyPositions) {
		return;
	}

	const project = projectId ? store.get(projectId) : null;
	const connection = project ? ConnectionHandler.getConnection(project, 'Project_tasks') : null;
	if (!connection) {
		return;
	}

	(connection.getLinkedRecords('edges') ?? []).forEach(edge => {
		const node = edge?.getLinkedRecord('node');
		if (!node || deleted.has(node.getDataID())) {
			return;
		}
		const setId = node.getLinkedRecord('taskSet')?.getDataID();
		const removed = setId ? removals.get(setId)?.positions : undefined;
		const position = numberOrNull(node.getValue('taskSetSequence'));
		if (!removed || removed.length === 0 || position == null) {
			return;
		}
		const shifted = shiftedPosition(position, removed);
		if (shifted !== position) {
			node.setValue(shifted, 'taskSetSequence');
		}
	});
};
